import { create } from 'zustand';
import { mattersApi } from '../services/api';
import { Matter, MatterDetailData } from '../types';

/**
 * Cache for the matters list (mirrors jobsStore) so the Cases page and the
 * CasePicker render instantly from cache while a refresh runs in the
 * background. Also caches per-matter detail payloads (jobs + documents) so
 * revisiting a matter renders instantly instead of blocking on a Turso
 * round-trip — same cache-then-revalidate idea as studioStore.
 */
interface MattersState {
  matters: Matter[];
  detailById: Record<string, MatterDetailData>;
  lastFetched: number | null;
  isFetching: boolean;
  fetchMatters: () => Promise<void>;
  /** Create a matter and prepend it to the cache; returns the new matter. */
  createMatter: (title: string, clientName?: string) => Promise<Matter>;
  /** Cache a detail payload and sync the list entry with it. */
  setMatterDetail: (detail: MatterDetailData) => void;
  /** Drop a matter from both caches (after delete). */
  clearMatter: (id: string) => void;
}

export const useMattersStore = create<MattersState>((set, get) => ({
  matters: [],
  detailById: {},
  lastFetched: null,
  isFetching: false,
  fetchMatters: async () => {
    if (get().isFetching) return;
    set({ isFetching: true });
    try {
      const { data } = await mattersApi.list();
      set({ matters: data, lastFetched: Date.now(), isFetching: false });
    } catch {
      set({ isFetching: false });
    }
  },
  createMatter: async (title, clientName) => {
    const { data } = await mattersApi.create({
      title,
      ...(clientName ? { client_name: clientName } : {}),
    });
    set((s) => ({ matters: [{ ...data, jobs_count: 0, documents_count: 0 }, ...s.matters] }));
    return data;
  },
  setMatterDetail: (detail) =>
    set((s) => ({
      detailById: { ...s.detailById, [detail.id]: detail },
      matters: s.matters.map((m) =>
        m.id === detail.id
          ? {
              ...m,
              title: detail.title,
              client_name: detail.client_name,
              description: detail.description,
              updated_at: detail.updated_at,
              jobs_count: detail.jobs.length,
              documents_count: detail.documents.length,
            }
          : m
      ),
    })),
  clearMatter: (id) =>
    set((s) => {
      const { [id]: _dropped, ...rest } = s.detailById;
      return { detailById: rest, matters: s.matters.filter((m) => m.id !== id) };
    }),
}));
